import type React from "react";
import {
  composeRenderProps,
  Modal,
  ModalOverlay,
  PopoverContext,
  Popover as RACPopover,
  type PopoverProps as RACPopoverProps,
  useSlottedContext,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { Dialog } from "./dialog";
import { useMediaQuery } from "./utils";

export interface PopoverProps
  extends Omit<RACPopoverProps, "children" | "className"> {
  children: React.ReactNode;
  className?: string | ((values: { defaultClassName?: string }) => string);
  respectScreen?: boolean;
  noEnteringAnimation?: boolean;
  noExitingAnimation?: boolean;
}

const popoverStyles = tv({
  base: [
    "z-50 overflow-y-auto overflow-x-hidden rounded-lg border border-border bg-popover p-4 text-popover-foreground shadow-lg shadow-black/5 outline-none",
    "will-change-[transform,opacity]",
  ],
  variants: {
    isEntering: {
      true: [
        "animate-in ease-cool fade-in duration-150",
        "data-[placement=left]:slide-in-from-right-1",
        "data-[placement=right]:slide-in-from-left-1",
        "data-[placement=top]:slide-in-from-bottom-1",
        "data-[placement=bottom]:slide-in-from-top-1",
      ],
    },
    isExiting: {
      true: [
        "animate-out ease-cool fade-out duration-150",
        "data-[placement=left]:slide-out-to-right-1",
        "data-[placement=right]:slide-out-to-left-1",
        "data-[placement=top]:slide-out-to-bottom-1",
        "data-[placement=bottom]:slide-out-to-top-1",
      ],
    },
  },
});

const drawerStyles = tv({
  base: [
    "fixed top-auto bottom-0 z-50 max-h-full w-full max-w-2xl border border-b-transparent bg-popover outline-hidden",
  ],
  variants: {
    isMenu: {
      true: "rounded-t-xl p-0 [&_[role=dialog]]:*:not-has-[[data-slot=dialog-body]]:px-1 w-full!",
      false: "rounded-t-2xl",
    },
    isEntering: {
      true: [
        "[will-change:transform]",
        "animate-in fade-in duration-150 slide-in-from-bottom ease-cool",
        "sm:slide-in-from-bottom-auto sm:slide-in-from-top-[20%]",
      ],
    },
    isExiting: {
      true: "slide-out-to-bottom animate-out duration-150 ease-in",
    },
  },
});

export function Popover({
  respectScreen = true,
  className,
  children,
  ...props
}: PopoverProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  // biome-ignore lint/style/noNonNullAssertion: it's ok
  const popoverContext = useSlottedContext(PopoverContext)!;
  const isSubmenuTrigger = popoverContext?.trigger === "SubmenuTrigger";
  const isMenuTrigger = popoverContext?.trigger === "MenuTrigger";
  const isMenu = isMenuTrigger || isSubmenuTrigger;

  let offset = 8;
  offset =
    props.offset !== undefined
      ? props.offset
      : isSubmenuTrigger
        ? offset - 14
        : offset;

  return isMobile && respectScreen ? (
    <ModalOverlay
      isDismissable
      className="fixed top-0 left-0 isolate z-102 h-(--visual-viewport-height) w-full bg-black/15 dark:bg-black/40 [--visual-viewport-vertical-padding:16px] animate-in fade-in duration-150 ease-cool"
    >
      <Modal
        className={composeRenderProps(className, (className, renderProps) =>
          drawerStyles({ ...renderProps, isMenu, className }),
        )}
      >
        <Dialog aria-label={props["aria-label"]} hideCloseButton>
          {children}
        </Dialog>
      </Modal>
    </ModalOverlay>
  ) : (
    <RACPopover
      {...props}
      offset={offset}
      className={composeRenderProps(className, (className, renderProps) =>
        popoverStyles({ ...renderProps, className }),
      )}
    >
      {children}
    </RACPopover>
  );
}
